import { useEffect, useMemo, useState } from 'react';

import { resolveTokensByContractIds } from 'api/amm';
import { getActiveRegistryVotingRequest } from 'api/asset-registry';
import { getActiveProposalsCount } from 'api/governance';

import { getAssetString } from 'helpers/assets';
import { getEnv } from 'helpers/env';
import { createAsset, getTokensFromCache } from 'helpers/token';

import useAssetsStore from 'store/assetsStore/useAssetsStore';

import { SorobanToken, TokenType } from 'types/token';

import {
    isHeaderProposal,
    isProposalCounts,
    readHeaderCache,
    writeHeaderCache,
} from './Header.cache';
import { HeaderIndicators, HeaderProposal } from './Header.types';

const useHeaderIndicators = (): HeaderIndicators => {
    const [counts, setCounts] = useState(
        () => readHeaderCache('counts', isProposalCounts) ?? { active: 0, discussion: 0 },
    );
    const [proposal, setProposal] = useState<HeaderProposal | null>(() =>
        readHeaderCache('registry', isHeaderProposal),
    );
    const { assetsInfo, processNewAssets } = useAssetsStore();

    useEffect(() => {
        let active = true;
        const network = getEnv();
        const isCurrent = () => active && getEnv() === network;
        getActiveProposalsCount()
            .then(value => {
                if (!isCurrent()) return;
                setCounts(value);
                writeHeaderCache('counts', value);
            })
            .catch(() => undefined);
        getActiveRegistryVotingRequest()
            .then(value => {
                if (!isCurrent()) return;
                if (!value) {
                    setProposal(null);
                    writeHeaderCache('registry', null);
                    return;
                }
                const {
                    id,
                    proposal_type,
                    asset_code,
                    asset_issuer,
                    asset_contract_address,
                    end_at,
                } = value;
                const next = {
                    id,
                    proposal_type,
                    asset_code,
                    asset_issuer,
                    asset_contract_address,
                    end_at,
                };
                const cached = readHeaderCache('registry', isHeaderProposal);
                const snapshot = { ...next, image: cached?.id === id ? cached.image : undefined };
                setProposal(snapshot);
                writeHeaderCache('registry', snapshot);
            })
            .catch(() => undefined);
        return () => {
            active = false;
        };
    }, []);

    // The header needs only the active proposal's asset, not the whole token catalog.
    useEffect(() => {
        if (!proposal) return;
        if (proposal.asset_code) {
            const asset = createAsset(proposal.asset_code, proposal.asset_issuer);
            if (!assetsInfo.has(getAssetString(asset))) processNewAssets([asset]);
            return;
        }
        if (!proposal.asset_contract_address) return;
        let active = true;
        const network = getEnv();
        resolveTokensByContractIds([proposal.asset_contract_address])
            .then(([token]) => {
                if (!active || getEnv() !== network || token?.type !== TokenType.soroban) return;
                if (token.logo !== undefined && token.logo !== proposal.image) {
                    const updated = { ...proposal, image: token.logo };
                    setProposal(updated);
                    writeHeaderCache('registry', updated);
                }
            })
            .catch(() => undefined);
        return () => {
            active = false;
        };
    }, [
        proposal?.id,
        proposal?.asset_code,
        proposal?.asset_issuer,
        proposal?.asset_contract_address,
        assetsInfo,
        processNewAssets,
    ]);

    const withImage = useMemo(() => {
        if (!proposal) return null;
        const image = proposal.asset_code
            ? assetsInfo.get(
                  getAssetString(createAsset(proposal.asset_code, proposal.asset_issuer)),
              )?.image
            : getTokensFromCache()?.find(
                  (token): token is SorobanToken =>
                      token.type === TokenType.soroban &&
                      token.contract === proposal.asset_contract_address,
              )?.logo;
        return image === undefined ? proposal : { ...proposal, image };
    }, [proposal, assetsInfo]);

    useEffect(() => {
        if (withImage && withImage.image !== proposal?.image)
            writeHeaderCache('registry', withImage);
    }, [withImage, proposal]);

    return { counts, proposal: withImage };
};

export default useHeaderIndicators;
